/**
 * Native catalog entity page with per-user tab preferences and owner tab visibility.
 *
 * Wires blueprint entity-content extensions into `EntityLayoutWrapper`, which
 * applies preference/owner filtering via public `EntityLayout` from
 * `@backstage/plugin-catalog`.
 */
import { useEffect, type ReactElement } from 'react';
import { coreExtensionData } from '@backstage/frontend-plugin-api';
import {
  useApi,
  useRouteRefParams,
  errorApiRef,
} from '@backstage/core-plugin-api';
import { EntityLayout } from '@backstage/plugin-catalog';
import {
  AsyncEntityProvider,
  catalogApiRef,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { Entity } from '@backstage/catalog-model';
import { useNavigate } from 'react-router-dom';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';
import { EntityLayoutWrapper } from './EntityPage';

type EntityPageExtensionInputs = {
  contents: Array<{ get(ref: unknown): unknown }>;
};

function useEntityFromUrl() {
  const { kind, namespace, name } = useRouteRefParams(entityRouteRef);
  const navigate = useNavigate();
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entity,
    error,
    loading,
    retry: refresh,
  } = useAsyncRetry(
    () => {
      if (!kind || !namespace || !name) {
        return Promise.resolve(undefined);
      }

      return catalogApi.getEntityByRef({ kind, namespace, name });
    },
    [catalogApi, kind, namespace, name],
  );

  useEffect(() => {
    const missingParams = [
      !namespace && 'namespace',
      !kind && 'kind',
      !name && 'name',
    ].filter(Boolean);

    if (missingParams.length > 0) {
      errorApi.post(
        new Error(`Missing entity route params: ${missingParams.join(', ')}`),
      );
      navigate('/');
    }
  }, [errorApi, kind, name, namespace, navigate]);

  return { entity, loading, error, refresh };
}

function buildRouteFilter(
  filterFunction?: (entity: Entity) => boolean,
): (entity: Entity) => boolean {
  return filterFunction ?? (() => true);
}

/**
 * Builds the catalog entity page element used by `catalogModule` page override.
 */
export function createCatalogEntityPageWithTabVisibility(
  inputs: EntityPageExtensionInputs,
) {
  const contentRoutes = inputs.contents.map(output => (
    <EntityLayout.Route
      key={String(output.get(coreExtensionData.routePath))}
      path={output.get(coreExtensionData.routePath) as string}
      title={output.get(EntityContentBlueprint.dataRefs.title) as string}
      if={buildRouteFilter(
        output.get(EntityContentBlueprint.dataRefs.filterFunction) as
          | ((entity: Entity) => boolean)
          | undefined,
      )}
    >
      {output.get(coreExtensionData.reactElement) as ReactElement}
    </EntityLayout.Route>
  ));

  const CatalogEntityPage = () => (
    <AsyncEntityProvider {...useEntityFromUrl()}>
      <EntityLayoutWrapper>{contentRoutes}</EntityLayoutWrapper>
    </AsyncEntityProvider>
  );

  return <CatalogEntityPage />;
}
